import { parseFillGapsBody, parseFillGapsChoiceBody, parseChecklistBody, checklistHasAutoKeys, isChecklistBoxChecked } from './gapParsing.js';
import { getExerciseTotalItems } from './exerciseUtils.js';
import {
    parseSortBoxesBody,
    computeSortBoxesGrade,
    collectErrorCorrectionItems,
    computeErrorCorrectionGrade,
} from './sortBoxesAndErrorCorrection.js';

export { normalizeAnswer } from './normalizeAnswer.js';
import { normalizeAnswer } from './normalizeAnswer.js';

const FILL_GAPS_SELF_CHECK_META = '__self_check_meta__';
const FILL_GAPS_AI_META = '__fill_gaps_ai_meta__';

function extractGapAnswersOnly(answersObj) {
    if (!answersObj || typeof answersObj !== 'object') return {};
    const {
        [FILL_GAPS_SELF_CHECK_META]: _sc,
        [FILL_GAPS_AI_META]: _ai,
        ...rest
    } = answersObj;
    return rest;
}

function readFillGapsSelfCheckMeta(answersObj) {
    return answersObj?.[FILL_GAPS_SELF_CHECK_META] || {};
}

function gapMatches(gapPart, studentVal) {
    const answers = (gapPart.answers || []).filter((a) => String(a || '').trim());
    if (!answers.length) return null; // no answer key → teacher must check
    return answers.some((a) => normalizeAnswer(a) === normalizeAnswer(studentVal || ''));
}

/** Mark emoji for a gap/item: ✅ / ❌ / 📝 (needs teacher check). */
export const MARK_NEEDS_TEACHER = '📝';

/**
 * @param {boolean|null|undefined} isCorrect
 * @param {{ hasAutoKey?: boolean, reviewMode?: boolean }} [opts]
 */
export function answerMarkEmoji(isCorrect, { hasAutoKey = true, reviewMode = false } = {}) {
    if (isCorrect === true) return '✅';
    if (isCorrect === false) {
        // Student view: never show ❌ for items without an answer key (often legacy auto-false).
        if (!hasAutoKey && !reviewMode) return MARK_NEEDS_TEACHER;
        return '❌';
    }
    if (!hasAutoKey || reviewMode) return MARK_NEEDS_TEACHER;
    return '';
}

/** Background/border tone for gap shells based on mark state. */
export function answerMarkTone(isCorrect, { hasAutoKey = true, reviewMode = false } = {}) {
    if (isCorrect === true) return 'ok';
    if (isCorrect === false && (hasAutoKey || reviewMode)) return 'bad';
    if (!hasAutoKey || (reviewMode && isCorrect !== true && isCorrect !== false)) return 'pending';
    if (isCorrect === false) return 'pending';
    return 'idle';
}

const MARK_TONE_COLORS = {
    ok: { bg: '#d1fae5', border: '#6ee7b7' },
    bad: { bg: '#fee2e2', border: '#fca5a5' },
    pending: { bg: '#fffbeb', border: '#fcd34d' },
    idle: { bg: '#fff', border: '#c7d2fe' },
    lockedIdle: { bg: '#f8fafc', border: '#cbd5e1' },
};

export function answerMarkColors(tone, { locked = false } = {}) {
    if (tone === 'idle' && locked) return MARK_TONE_COLORS.lockedIdle;
    return MARK_TONE_COLORS[tone] || MARK_TONE_COLORS.idle;
}

function parseOrderWordsLines(body) {
    return (body || '').split('\n').filter((l) => l.trim()).map((line, idx) => {
        const m = line.match(/^(\d+)\.\s*(.+)/);
        const content = m ? m[2] : line;
        const words = content.split('/').map((w) => w.trim()).filter(Boolean);
        return { idx, original: words.join(' ') };
    });
}

/** One item per line for Order (Lines). Optional leading `1. ` numbers are stripped. */
export function parseOrderLinesItems(body) {
    return (body || '').split('\n').filter((l) => l.trim()).map((line, idx) => {
        const m = line.match(/^(\d+)\.\s*(.+)/);
        const text = (m ? m[2] : line).trim();
        return { id: idx, text };
    });
}

/** Student answer for Order (Lines): `{ order: number[] }` of original line ids. */
export function getOrderLinesOrder(answers) {
    if (Array.isArray(answers)) return answers.map((n) => Number(n));
    if (answers && Array.isArray(answers.order)) return answers.order.map((n) => Number(n));
    return null;
}

/** Parse `word // image_url` lines used by image gap-fill and image matching. */
export function parseImageExerciseItems(body) {
    return (body || '').split('\n').filter((l) => l.trim()).map((line, idx) => {
        const separatorIndex = line.indexOf('//');
        const word = separatorIndex === -1
            ? line.trim()
            : line.substring(0, separatorIndex).trim();
        return { idx, word };
    });
}

/** Parse `Left // Right` lines for Matching (columns). */
export function parseMatchingColumnsPairs(body) {
    return (body || '').split('\n').filter((l) => l.trim()).map((line, idx) => {
        const separatorIndex = line.indexOf('//');
        if (separatorIndex === -1) {
            return { idx, left: line.trim(), right: '' };
        }
        return {
            idx,
            left: line.substring(0, separatorIndex).trim(),
            right: line.substring(separatorIndex + 2).trim(),
        };
    }).filter((p) => p.left);
}

function computeImageExerciseGrade(exercise, answers) {
    const items = parseImageExerciseItems(exercise.body || '');
    const is_correct = Object.fromEntries(
        items.map(({ idx, word }) => {
            const studentVal = answers[idx] ?? answers[String(idx)] ?? '';
            return [String(idx), normalizeAnswer(studentVal) === normalizeAnswer(word)];
        })
    );
    const score = Object.values(is_correct).filter(Boolean).length;
    return { is_correct, score, total: items.length };
}

function computeMatchingColumnsGrade(exercise, answers) {
    const pairs = parseMatchingColumnsPairs(exercise.body || '');
    const is_correct = Object.fromEntries(
        pairs.map(({ idx, right }) => {
            const studentVal = answers[idx] ?? answers[String(idx)] ?? '';
            return [String(idx), normalizeAnswer(studentVal) === normalizeAnswer(right)];
        })
    );
    const score = Object.values(is_correct).filter(Boolean).length;
    return { is_correct, score, total: pairs.length };
}

/** True when at least one gap/item has a non-empty answer key the system can grade against. */
export function exerciseHasAutoAnswerKeys(ex) {
    if (!ex) return false;
    if (['fill_gaps', 'fill_gaps_words', 'fill_gaps_self_check'].includes(ex.type)) {
        const parts = parseFillGapsBody(ex.body || '');
        return parts.some((p) => {
            if (p.type !== 'gap') return false;
            if (p.answers?.some((a) => String(a || '').trim())) return true;
            // Given-words: prompt alone is the key when answers[] is empty
            if (ex.type === 'fill_gaps_words' && String(p.prompt || '').trim()) return true;
            return false;
        });
    }
    if (ex.type === 'fill_gaps_choice') {
        const items = parseFillGapsChoiceBody(ex.body || '');
        return items.some((item) => {
            if (item.type === 'gap') return item.answers?.some((a) => String(a || '').trim());
            if (item.type === 'choice') return (item.options || []).some((o) => o.correct && String(o.label || '').trim());
            if (item.type === 'check') return item.expected === true || item.expected === false;
            return false;
        });
    }
    if (ex.type === 'order_words' || ex.type === 'order_lines') {
        return (ex.body || '').split('\n').some((l) => l.trim());
    }
    if (ex.type === 'dictation') {
        return (ex.body || '').split('\n').some((l) => l.trim());
    }
    if (['image_fill_gaps', 'image_matching'].includes(ex.type)) {
        return (ex.body || '').split('\n').some((l) => l.trim());
    }
    if (ex.type === 'matching_columns') {
        return (ex.body || '').split('\n').some((l) => l.includes('//'));
    }
    if (ex.type === 'sort_boxes') {
        const { items, boxes } = parseSortBoxesBody(ex.body || '');
        return boxes.length > 0 && items.some((it) => it.box);
    }
    if (ex.type === 'error_correction') {
        return collectErrorCorrectionItems(ex.body || '').some((it) => it.answers?.length);
    }
    if (ex.type === 'checklist') {
        return checklistHasAutoKeys(ex.body || '');
    }
    return false;
}

/** True when the exercise type supports system auto-grading from body answers. */
export function exerciseNeedsAutoConfirm(ex) {
    return exerciseHasAutoAnswerKeys(ex);
}

export function computeRewardsFromScore(exercise, score) {
    if (!exercise) return { awarded_stars: 0, awarded_coins: 0 };
    const assessment = exercise.assessment || {};
    const starsPerGap = parseFloat(assessment.starsPerGap) || 0;
    const coinsPerGap = parseInt(assessment.coinsPerGap, 10) || 0;
    const starsOnComplete = parseFloat(assessment.starsOnComplete) || 0;
    const coinsOnComplete = parseInt(assessment.coinsOnComplete, 10) || 0;
    const safeScore = parseFloat(score) || 0;
    return {
        awarded_stars: (safeScore * starsPerGap) + starsOnComplete,
        awarded_coins: (safeScore * coinsPerGap) + coinsOnComplete,
    };
}

/** Max stars/coins a teacher can award for one gap/item (from assessment.perGap). */
export function getPerGapRewardCaps(exercise) {
    const assessment = exercise?.assessment || {};
    return {
        maxStars: parseFloat(assessment.starsPerGap) || 0,
        maxCoins: parseInt(assessment.coinsPerGap, 10) || 0,
    };
}

/** True when awarded stars/coins equal the per-gap maximum (partial credit = not max). */
export function isMaxGapReward(stars, coins, maxStars, maxCoins) {
    const sCap = Number(maxStars) || 0;
    const cCap = Number(maxCoins) || 0;
    if (sCap <= 0 && cCap <= 0) return false;
    const s = Number(stars) || 0;
    const c = Number(coins) || 0;
    const starsFull = sCap <= 0 || s >= sCap;
    const coinsFull = cCap <= 0 || c >= cCap;
    return starsFull && coinsFull;
}

/** Build per-gap reward map: correct → max, wrong → 0. */
export function buildGapRewardsFromIsCorrect(exercise, isCorrectMap, itemCount) {
    const { maxStars, maxCoins } = getPerGapRewardCaps(exercise);
    const out = {};
    const n = Math.max(0, Number(itemCount) || 0);
    for (let i = 0; i < n; i++) {
        const k = String(i);
        const ok = isCorrectMap?.[i] === true || isCorrectMap?.[k] === true;
        out[k] = { stars: ok ? maxStars : 0, coins: ok ? maxCoins : 0 };
    }
    return out;
}

/** Sum per-gap rewards + onComplete bonuses. */
export function sumGapRewards(gapRewards, exercise) {
    let stars = 0;
    let coins = 0;
    Object.values(gapRewards || {}).forEach((r) => {
        if (!r || typeof r !== 'object') return;
        stars += parseFloat(r.stars) || 0;
        coins += parseInt(r.coins, 10) || 0;
    });
    const assessment = exercise?.assessment || {};
    stars += parseFloat(assessment.starsOnComplete) || 0;
    coins += parseInt(assessment.coinsOnComplete, 10) || 0;
    return { awarded_stars: stars, awarded_coins: coins };
}

/** Prefer explicit gap_rewards when present; otherwise score × perGap. */
export function resolveExerciseRewards(exercise, { score, gap_rewards } = {}) {
    if (gap_rewards && typeof gap_rewards === 'object' && Object.keys(gap_rewards).length > 0) {
        return sumGapRewards(gap_rewards, exercise);
    }
    return computeRewardsFromScore(exercise, score);
}

/**
 * Computes is_correct map and score from stored student answers.
 * @returns {{ is_correct: Record<string, boolean>, score: number, total: number }}
 */
export function computeAutoGrade(exercise, answers) {
    if (!exercise) {
        return { is_correct: {}, score: 0, total: 0 };
    }

    const type = exercise.type;
    const body = exercise.body || '';

    if (type === 'checklist') {
        const items = parseChecklistBody(body);
        const boxAnswers = answers && typeof answers === 'object' ? answers : {};
        const is_correct = Object.fromEntries(items.map((item) => {
            if (item.expected !== true && item.expected !== false) return [String(item.idx), null];
            return [String(item.idx), isChecklistBoxChecked(boxAnswers, item.idx) === item.expected];
        }));
        const score = Object.values(is_correct).filter((v) => v === true).length;
        return { is_correct, score, total: items.length };
    }

    if (!answers || typeof answers !== 'object') {
        return { is_correct: {}, score: 0, total: 0 };
    }

    if (['fill_gaps', 'fill_gaps_words'].includes(type)) {
        const parts = parseFillGapsBody(body);
        const gaps = parts.filter((p) => p.type === 'gap').map((g) => {
            if (type === 'fill_gaps_words' && g.answers.length === 0 && g.prompt) {
                return { ...g, answers: [g.prompt], prompt: '' };
            }
            return g;
        });
        const gapAnswers = extractGapAnswersOnly(answers);
        const is_correct = Object.fromEntries(
            gaps.map((g, i) => [String(i), gapMatches(g, gapAnswers[i] ?? gapAnswers[String(i)])])
        );
        const score = Object.values(is_correct).filter((v) => v === true).length;
        return { is_correct, score, total: gaps.length };
    }

    if (type === 'fill_gaps_self_check') {
        const parts = parseFillGapsBody(body);
        const gaps = parts.filter((p) => p.type === 'gap');
        const meta = readFillGapsSelfCheckMeta(answers);
        const selfCheck = meta.student_self_check || {};
        const is_correct = Object.fromEntries(
            gaps.map((_, i) => [String(i), selfCheck[i] === true || selfCheck[String(i)] === true])
        );
        const score = Object.values(is_correct).filter((v) => v === true).length;
        return { is_correct, score, total: gaps.length };
    }

    // Student-side AI grading — do not string-match keys; prefer assignment.is_correct.
    if (type === 'fill_gaps_ai') {
        const stripped = String(body || '').replace(/\{ai:check\}/gi, '');
        const parts = parseFillGapsBody(stripped);
        const gaps = parts.filter((p) => p.type === 'gap');
        return { is_correct: {}, score: 0, total: gaps.length };
    }

    if (type === 'fill_gaps_choice') {
        const items = parseFillGapsChoiceBody(body);
        const gapAnswers = extractGapAnswersOnly(answers);
        const is_correct = Object.fromEntries(items.map((item) => {
            const i = item.idx;
            const studentVal = gapAnswers[i] ?? gapAnswers[String(i)] ?? '';
            if (item.type === 'choice') {
                const correct = (item.options || []).find((o) => o.correct)?.label || '';
                if (!String(correct).trim()) return [String(i), null];
                return [String(i), normalizeAnswer(studentVal) === normalizeAnswer(correct)];
            }
            if (item.type === 'check') {
                if (item.expected !== true && item.expected !== false) return [String(i), null];
                return [String(i), isChecklistBoxChecked(gapAnswers, i) === item.expected];
            }
            return [String(i), gapMatches(item, studentVal)];
        }));
        const score = Object.values(is_correct).filter((v) => v === true).length;
        return { is_correct, score, total: items.length };
    }

    if (type === 'order_words') {
        const sentences = parseOrderWordsLines(body);
        const is_correct = Object.fromEntries(
            sentences.map(({ idx, original }) => {
                const arranged = answers[idx] ?? answers[String(idx)];
                let userAnswer = '';
                if (Array.isArray(arranged)) {
                    userAnswer = arranged.map((w) => (typeof w === 'string' ? w : w.text)).join(' ');
                } else if (typeof arranged === 'string') {
                    userAnswer = arranged;
                }
                return [String(idx), normalizeAnswer(userAnswer) === normalizeAnswer(original)];
            })
        );
        const score = Object.values(is_correct).filter(Boolean).length;
        return { is_correct, score, total: sentences.length };
    }

    if (type === 'order_lines') {
        const items = parseOrderLinesItems(body);
        const order = getOrderLinesOrder(answers);
        // Score each list slot: item at position i must be original id i.
        const is_correct = Object.fromEntries(
            items.map(({ id }) => {
                const placedId = order ? order[id] : undefined;
                return [String(id), placedId === id];
            })
        );
        const score = Object.values(is_correct).filter(Boolean).length;
        return { is_correct, score, total: items.length };
    }

    if (['image_fill_gaps', 'image_matching'].includes(type)) {
        return computeImageExerciseGrade(exercise, answers);
    }

    if (type === 'matching_columns') {
        return computeMatchingColumnsGrade(exercise, answers);
    }

    if (type === 'sort_boxes') {
        return computeSortBoxesGrade(exercise, answers);
    }

    if (type === 'error_correction') {
        return computeErrorCorrectionGrade(exercise, answers);
    }

    if (type === 'dictation') {
        const lines = body.split('\n').filter((l) => l.trim());
        const is_correct = Object.fromEntries(
            lines.map((line, i) => {
                const m = line.match(/\[(.*?)\]/);
                const answer = m && m[1] ? m[1].trim() : line.trim();
                const studentVal = answers[i] ?? answers[String(i)] ?? '';
                return [String(i), normalizeAnswer(studentVal) === normalizeAnswer(answer)];
            })
        );
        const score = Object.values(is_correct).filter(Boolean).length;
        return { is_correct, score, total: lines.length };
    }

    return { is_correct: {}, score: 0, total: 0 };
}

function readBooleanMark(map, key) {
    if (!map || typeof map !== 'object') return undefined;
    if (map[key] === true || map[String(key)] === true) return true;
    if (map[key] === false || map[String(key)] === false) return false;
    return undefined;
}

function mergeIsCorrectMaps(autoMap, storedMap) {
    const merged = {};
    const keys = new Set([
        ...Object.keys(autoMap || {}),
        ...Object.keys(storedMap && typeof storedMap === 'object' ? storedMap : {}),
    ]);
    keys.forEach((k) => {
        const stored = readBooleanMark(storedMap, k);
        const auto = readBooleanMark(autoMap, k);
        if (stored !== undefined) merged[String(k)] = stored;
        else if (auto !== undefined) merged[String(k)] = auto;
    });
    return merged;
}

function countCorrectMarks(isCorrectMap, total) {
    if (Number(total) > 0) {
        let n = 0;
        for (let i = 0; i < total; i++) {
            if (isCorrectMap?.[i] === true || isCorrectMap?.[String(i)] === true) n++;
        }
        return n;
    }
    return Object.values(isCorrectMap || {}).filter((v) => v === true).length;
}

/**
 * Confirm-auto payload: auto-grade as baseline, then keep any teacher marks
 * (and per-gap rewards) already saved on the assignment. Confirm must not
 * wipe toggles the teacher made in the preview before clicking the button.
 */
export function confirmAutoGradeWithOverrides(exercise, assignment) {
    const auto = computeAutoGrade(exercise, assignment?.answers);
    const is_correct = mergeIsCorrectMaps(auto.is_correct, assignment?.is_correct);
    const total = auto.total || getExerciseTotalItems(exercise) || Object.keys(is_correct).length;
    const score = countCorrectMarks(is_correct, total);
    const gap_rewards = buildGapRewardsFromIsCorrect(exercise, is_correct, total);
    const storedRewards = assignment?.gap_rewards;
    if (storedRewards && typeof storedRewards === 'object') {
        Object.entries(storedRewards).forEach(([k, r]) => {
            if (r && typeof r === 'object') gap_rewards[String(k)] = r;
        });
    }
    const summed = sumGapRewards(gap_rewards, exercise);
    return {
        is_correct,
        score,
        total,
        gap_rewards,
        awarded_stars: summed.awarded_stars,
        awarded_coins: summed.awarded_coins,
    };
}

/**
 * Single source of truth for teacher review score display.
 * Prefers stored is_correct; falls back to auto-computing from answers when missing.
 */
export function resolveAssignmentScore(assignment, exercise) {
    const total = getExerciseTotalItems(exercise) || 0;
    if (!assignment || !exercise) {
        return { score: 0, total, is_correct: {}, preview: false };
    }

    const storedMap = assignment.is_correct;
    const hasStoredMap = storedMap
        && typeof storedMap === 'object'
        && Object.keys(storedMap).length > 0;

    if (hasStoredMap) {
        const fromMap = Object.values(storedMap).filter((v) => v === true).length;
        return {
            score: typeof assignment.score === 'number' ? assignment.score : fromMap,
            total: total || Object.keys(storedMap).length,
            is_correct: storedMap,
            preview: !assignment.teacher_confirmed && exerciseNeedsAutoConfirm(exercise),
        };
    }

    const computed = computeAutoGrade(exercise, assignment.answers);
    if (computed.total > 0 && assignment.answers) {
        return {
            score: computed.score,
            total: computed.total,
            is_correct: computed.is_correct,
            preview: !assignment.teacher_confirmed && exerciseNeedsAutoConfirm(exercise),
        };
    }

    return {
        score: assignment.score ?? 0,
        total,
        is_correct: {},
        preview: false,
    };
}
